import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Loader2, Plus, X, Edit2, Download, HardHat, LogOut, RotateCcw, Truck, Brush, FileSpreadsheet } from "lucide-react";

const CATEGORY_META = {
  driver:        { label: "Drivers",                       icon: Truck,   bg: "#DCE9F0", accent: "#1F4863", order: 1 },
  cleaner:       { label: "Cleaner / Scavenger",           icon: Brush,   bg: "#FCE7C9", accent: "#9A6020", order: 2 },
  worker_ponnal: { label: "Workers – Ponnal",              icon: HardHat, bg: "#D8F3DC", accent: "#1B4332", order: 3 },
  worker_bihar:  { label: "Contract Labour (from Bihar)",  icon: HardHat, bg: "#E5E7EB", accent: "#57534E", order: 4 },
  worker_other:  { label: "Other Workers",                 icon: HardHat, bg: "#F4F1DE", accent: "#78716C", order: 5 },
};

const CATEGORY_LIST = Object.entries(CATEGORY_META)
  .sort((a, b) => a[1].order - b[1].order)
  .map(([key, m]) => ({ key, ...m }));

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [tab, setTab] = useState("salary"); // salary | list
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingWorker, setEditingWorker] = useState(null);
  const [exiting, setExiting] = useState(null);

  const load = async () => {
    setLoading(true);
    const [w, p] = await Promise.all([
      api.get("/workers"),
      api.get("/worker-payroll", { params: { month } }),
    ]);
    setWorkers(w.data); setRows(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, [month]);

  const exportCsv = () => {
    const headers = ["S.No","Code","Name","Designation","CTC","Monthly","Days Present","Earned Salary","Day Rate","Sun/Fest Days","Sun/Fest Earned","Hourly Earned","Total Payable","Remarks"];
    const csv = [headers.join(",")].concat(rows.map((r, i) => [
      i+1, r.worker_code, `"${r.name}"`, r.designation, r.ctc_annual, r.monthly,
      r.days_present, r.earned_salary, r.day_rate, r.sun_fest_days, r.sun_fest_earned,
      r.hourly_earned, r.total_payable, `"${r.remarks||""}"`,
    ].join(","))).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `workers_salary_${month}.csv`; a.click();
  };

  const total = useMemo(() => rows.reduce((s, r) => s + (r.total_payable || 0), 0), [rows]);
  const activeCount = workers.filter((w) => w.status === "active").length;
  const leftCount = workers.filter((w) => w.status === "left").length;

  // Group salary rows by worker category (Drivers / Cleaner / Ponnal / Bihar / Other)
  const groupedRows = useMemo(() => {
    const workerMap = Object.fromEntries(workers.map((w) => [w.id, w]));
    const buckets = {};
    rows.forEach((r) => {
      const w = workerMap[r.worker_id] || {};
      const cat = w.category || "worker_ponnal";
      (buckets[cat] = buckets[cat] || []).push(r);
    });
    return CATEGORY_LIST
      .filter((c) => buckets[c.key]?.length)
      .map((c) => ({
        key: c.key,
        meta: c,
        rows: [...buckets[c.key]].sort((a, b) => (a.worker_code || "").localeCompare(b.worker_code || "")),
      }));
  }, [rows, workers]);

  const exportXlsx = async () => {
    const { data } = await api.get("/worker-payroll/export.xlsx", {
      params: { month },
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `workers_salary_${month}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reactivate = async (w) => {
    if (!window.confirm(`Reactivate ${w.name} (${w.worker_code})?\n\nThe worker will be included in upcoming salary statements again.`)) return;
    await api.put(`/workers/${w.id}`, { status: "active", exit_date: null, exit_reason: null });
    load();
  };

  return (
    <div className="space-y-6" data-testid="workers-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Daily-wage workforce</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Workers</h1>
          <p className="text-stone-600 mt-1 text-sm">{activeCount} active · {leftCount} left · monthly salary statement separate from regular employees.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportXlsx} className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium" style={{ background: "#1B4332" }} data-testid="export-workers-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportCsv} className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="export-workers">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => setShowAdd(true)} data-testid="add-worker-button"
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
            <Plus className="w-4 h-4" /> Add Worker
          </button>
        </div>
      </header>

      <div className="flex items-center gap-1 border-b border-stone-200">
        {[["salary", "Salary Statement"], ["list", "Workers List"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`workers-tab-${k}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-current" : "border-transparent text-stone-500 hover:text-stone-800"}`}
                  style={tab === k ? { color: "var(--color-primary)" } : {}}>
            {l}
          </button>
        ))}
      </div>

      {tab === "salary" && (
        <>
          <div className="flex items-center gap-3">
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                   className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white" data-testid="workers-month" />
            <div className="ml-auto text-sm">
              <span className="text-stone-500">Total Payable: </span>
              <span className="font-heading text-lg font-bold" style={{ color: "var(--color-primary)" }}>{formatINR(total)}</span>
            </div>
          </div>

          <div className="card-flat p-4">
            <div className="text-center font-heading text-sm font-bold mb-4">
              Navaratnaa Cropscience Pvt. Ltd., Ponnal — Workers Salary Statement for {new Date(month + "-01").toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </div>
            {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
              rows.length === 0 ? (
                <div className="py-10 text-center text-stone-500">
                  <HardHat className="w-10 h-10 mx-auto text-stone-300 mb-2" />
                  No workers found.<br />
                  <button onClick={() => setShowAdd(true)} className="mt-3 px-3 py-1.5 rounded border border-primary text-primary text-xs font-medium">Add your first worker</button>
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedRows.map((grp) => {
                    const Icon = grp.meta.icon;
                    const subtotal = grp.rows.reduce((s, r) => s + (r.total_payable || 0), 0);
                    return (
                      <div key={grp.key} data-testid={`category-block-${grp.key}`}>
                        <div className="rounded-t-md px-4 py-2.5 flex items-center justify-between"
                             style={{ background: grp.meta.accent, color: "white" }}>
                          <div className="flex items-center gap-2 font-heading font-bold uppercase tracking-wider text-sm">
                            <Icon className="w-4 h-4" /> {grp.meta.label}
                            <span className="ml-1 text-[10px] font-medium opacity-80">· {grp.rows.length} member{grp.rows.length > 1 ? "s" : ""}</span>
                          </div>
                          <div className="text-sm font-bold">Subtotal: {formatINR(subtotal)}</div>
                        </div>
                        <div className="overflow-x-auto border-x border-b border-stone-200 rounded-b-md">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="font-bold" style={{ background: grp.meta.bg, color: grp.meta.accent }}>
                                <Th>S.No</Th><Th>Code</Th><Th>Name</Th><Th>Designation</Th>
                                <Th>CTC</Th><Th>Monthly</Th>
                                <Th>Days</Th>
                                <Th>Earned Salary</Th>
                                <Th>Sun/Fest Days</Th><Th>Sun/Fest ₹</Th>
                                <Th>Day Rate</Th><Th>Hourly ₹</Th>
                                <Th>Total Payable</Th>
                                <Th>Remarks</Th><Th></Th>
                              </tr>
                            </thead>
                            <tbody>
                              {grp.rows.map((r, i) => (
                                <tr key={r.id} className={i % 2 ? "bg-stone-50/60" : ""}>
                                  <Td center>{i + 1}</Td>
                                  <Td mono>{r.worker_code}</Td>
                                  <Td strong>{r.name}</Td>
                                  <Td>{r.designation}</Td>
                                  <Td right>{r.ctc_annual?.toLocaleString("en-IN")}</Td>
                                  <Td right>{r.monthly?.toLocaleString("en-IN")}</Td>
                                  <Td center>{r.days_present}</Td>
                                  <Td right strong>{r.earned_salary?.toLocaleString("en-IN")}</Td>
                                  <Td center>{r.sun_fest_days}</Td>
                                  <Td right>{r.sun_fest_earned?.toLocaleString("en-IN") || 0}</Td>
                                  <Td right>{r.day_rate}</Td>
                                  <Td right>{r.hourly_earned?.toLocaleString("en-IN") || 0}</Td>
                                  <Td right strong style={{ color: grp.meta.accent }}>{r.total_payable?.toLocaleString("en-IN")}</Td>
                                  <Td className="max-w-[10rem] truncate">{r.remarks || "—"}</Td>
                                  <Td>
                                    <button onClick={() => setEditing(r)} data-testid={`edit-worker-payroll-${r.id}`}
                                            className="p-1 rounded hover:bg-stone-100"><Edit2 className="w-3.5 h-3.5" /></button>
                                  </Td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                  {/* Grand Total */}
                  <div className="rounded-md px-4 py-3 flex items-center justify-between text-white font-heading" style={{ background: "var(--color-primary)" }}>
                    <div className="uppercase tracking-wider text-sm font-bold">Grand Total ({rows.length} workers)</div>
                    <div className="text-lg font-bold">{formatINR(total)}</div>
                  </div>
                </div>
              )
            )}
          </div>
        </>
      )}

      {tab === "list" && (
        <div className="card-flat p-0 overflow-hidden">
          {loading ? <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="workers-list-table">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
                  <th className="px-5 py-3">Code</th><th>Name</th><th>Designation</th><th>Mobile</th>
                  <th>Joined</th><th>Status</th><th>Exit Details</th><th className="px-4"></th>
                </tr></thead>
                <tbody>
                  {workers.length === 0 && <tr><td colSpan="8" className="py-10 text-center text-stone-500">No workers yet.</td></tr>}
                  {workers.map((w) => (
                    <tr key={w.id} className="border-t border-stone-100">
                      <td className="px-5 py-3 font-mono text-xs">{w.worker_code}</td>
                      <td className="font-medium">{w.name}</td>
                      <td className="text-stone-600">{w.designation}</td>
                      <td className="text-stone-600 text-xs">{w.mobile || "—"}</td>
                      <td className="text-stone-600 text-xs">{w.joining_date}</td>
                      <td>
                        <span className="status-pill capitalize" data-testid={`worker-status-${w.id}`}
                              style={w.status === "active" ? { background: "#D8F3DC", color: "#1B4332" } : { background: "#FADED7", color: "#9A3412" }}>
                          {w.status === "active" ? "Active" : "Left"}
                        </span>
                      </td>
                      <td className="text-xs text-stone-600 max-w-[14rem]">
                        {w.status === "left" ? <>{w.exit_date}<div className="text-stone-500 truncate">{w.exit_reason || ""}</div></> : "—"}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setEditingWorker(w)} data-testid={`edit-worker-${w.id}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 mr-1 text-xs font-medium rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50">
                          <Edit2 className="w-3.5 h-3.5" /> Edit
                        </button>
                        {w.status === "active" ? (
                          <button onClick={() => setExiting(w)} data-testid={`exit-worker-${w.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-orange-300 text-orange-800 hover:bg-orange-50">
                            <LogOut className="w-3.5 h-3.5" /> Mark as Left
                          </button>
                        ) : (
                          <button onClick={() => reactivate(w)} data-testid={`reactivate-worker-${w.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50">
                            <RotateCcw className="w-3.5 h-3.5" /> Reactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showAdd && <WorkerForm onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editingWorker && <WorkerForm existing={editingWorker} onClose={() => setEditingWorker(null)} onSaved={() => { setEditingWorker(null); load(); }} />}
      {editing && <PayrollEdit row={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {exiting && <ExitWorkerModal worker={exiting} onClose={() => setExiting(null)} onSaved={() => { setExiting(null); load(); }} />}
    </div>
  );
}

function ExitWorkerModal({ worker, onClose, onSaved }) {
  const [form, setForm] = useState({
    exit_date: new Date().toISOString().slice(0, 10),
    exit_reason: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.put(`/workers/${worker.id}`, { status: "left", exit_date: form.exit_date, exit_reason: form.exit_reason });
    setBusy(false); onSaved();
  };
  return (
    <Modal title={`Mark as Left · ${worker.name}`} onClose={onClose} testid="exit-worker-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-stone-600 p-3 rounded-md" style={{ background: "#FEFAE0" }}>
          {worker.worker_code} · {worker.designation}. The worker will be removed from upcoming salary statements but past records are kept.
        </div>
        <In label="Exit / Last Working Date" type="date" v={form.exit_date} on={(v) => setForm({ ...form, exit_date: v })} required />
        <label><div className="kpi-label mb-1.5">Reason for leaving</div>
          <textarea rows="3" value={form.exit_reason} onChange={(e) => setForm({ ...form, exit_reason: e.target.value })}
                    placeholder="e.g. Resigned, moved to native place, absconding…" data-testid="exit-reason-input"
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>
        <button type="submit" disabled={busy} data-testid="confirm-exit-worker-button"
                className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "#9A3412" }}>
          {busy ? "Saving…" : "Confirm Exit"}
        </button>
      </form>
    </Modal>
  );
}

function Th({ children, bg }) {
  return <th className="px-2 py-2 border border-stone-200 whitespace-nowrap text-stone-700"
             style={{ background: bg || "#F4F1DE", fontSize: 10 }}>{children}</th>;
}
function Td({ children, center, right, mono, strong, bg, className = "", style }) {
  return <td className={`px-2 py-1.5 border border-stone-100 ${center ? "text-center" : right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${strong ? "font-semibold" : ""} ${className}`}
             style={{ background: bg, fontSize: 11, ...style }}>{children}</td>;
}

function WorkerForm({ existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    name: existing?.name || "",
    designation: existing?.designation || "Worker",
    category: existing?.category || "worker_ponnal",
    worker_type: existing?.worker_type || "regular",
    ctc_annual: existing?.ctc_annual ?? 120000,
    daily_rate: existing?.daily_rate ?? 0,
    hourly_rate: existing?.hourly_rate ?? 0,
    mobile: existing?.mobile || "",
    location: existing?.location || "",
    aadhaar: existing?.aadhaar || "",
    bank_name: existing?.bank_name || "",
    account_number: existing?.account_number || "",
    ifsc: existing?.ifsc || "",
    joining_date: existing?.joining_date || new Date().toISOString().slice(0, 10),
    status: existing?.status || "active",
    remarks: existing?.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    const payload = {
      ...form,
      ctc_annual: Number(form.ctc_annual) || 0,
      daily_rate: Number(form.daily_rate) || 0,
      hourly_rate: Number(form.hourly_rate) || 0,
    };
    if (isEdit) {
      await api.put(`/workers/${existing.id}`, payload);
    } else {
      await api.post("/workers", payload);
    }
    setBusy(false); onSaved();
  };
  return (
    <Modal title={isEdit ? `Edit Worker · ${existing.worker_code}` : "Add Worker"} onClose={onClose} testid="worker-modal">
      <form onSubmit={submit} className="grid grid-cols-2 gap-3">
        <In label="Name" v={form.name} on={(v) => setForm({ ...form, name: v })} required />
        <In label="Designation" v={form.designation} on={(v) => setForm({ ...form, designation: v })} />
        <label className="block col-span-2">
          <div className="kpi-label mb-1.5">Category</div>
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="worker-category"
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option value="driver">🚛 Driver</option>
            <option value="cleaner">🧹 Cleaner / Scavenger</option>
            <option value="worker_ponnal">👷 Worker – Ponnal</option>
            <option value="worker_bihar">👷 Contract Labour (Bihar)</option>
            <option value="worker_other">👷 Other Worker</option>
          </select>
        </label>
        <label className="block">
          <div className="kpi-label mb-1.5">Wage Type</div>
          <select value={form.worker_type} onChange={(e) => setForm({ ...form, worker_type: e.target.value })} data-testid="worker-wage-type"
                  className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white">
            <option value="regular">Monthly (CTC)</option>
            <option value="contract">Daily Rate (Contract)</option>
          </select>
        </label>
        {form.worker_type === "contract" ? (
          <In label="Daily Rate ₹" type="number" v={form.daily_rate} on={(v) => setForm({ ...form, daily_rate: v })} required />
        ) : (
          <In label="CTC (Annual ₹)" type="number" v={form.ctc_annual} on={(v) => setForm({ ...form, ctc_annual: v })} required />
        )}
        <In label="Hourly Rate (₹/hr)" type="number" v={form.hourly_rate} on={(v) => setForm({ ...form, hourly_rate: v })} placeholder="blank = day rate ÷ 8" />
        <In label="Mobile" v={form.mobile} on={(v) => setForm({ ...form, mobile: v })} />
        <In label="Location" v={form.location} on={(v) => setForm({ ...form, location: v })} />
        <In label="Aadhaar" v={form.aadhaar} on={(v) => setForm({ ...form, aadhaar: v })} />
        <In label="Bank Name" v={form.bank_name} on={(v) => setForm({ ...form, bank_name: v })} />
        <In label="Account No." v={form.account_number} on={(v) => setForm({ ...form, account_number: v })} />
        <In label="IFSC" v={form.ifsc} on={(v) => setForm({ ...form, ifsc: v })} />
        <In label="Joining Date" type="date" v={form.joining_date} on={(v) => setForm({ ...form, joining_date: v })} />
        <button type="submit" disabled={busy} data-testid="save-worker-button"
                className="col-span-2 py-2 mt-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : isEdit ? "Save Changes" : "Add Worker"}
        </button>
      </form>
    </Modal>
  );
}

function PayrollEdit({ row, onClose, onSaved }) {
  // Standard hourly rate = day_rate / 8 (8-hour workday). User can override if needed.
  const derivedRate = row.day_rate ? Math.round(row.day_rate / 8) : 0;
  const [form, setForm] = useState({
    days_present: row.days_present || 30,
    sun_fest_days: row.sun_fest_days || 0,
    sun_fest_earned: row.sun_fest_earned || 0,
    hours: row.hours || 0,
    hourly_rate: row.hourly_rate || derivedRate,
    hourly_earned: row.hourly_earned || 0,
    remarks: row.remarks || "",
  });
  const [busy, setBusy] = useState(false);
  // Live auto-calc: if hours > 0 and rate > 0, hourly_earned = hours * rate
  const autoHourly = useMemo(() => {
    const h = Number(form.hours) || 0;
    const r = Number(form.hourly_rate) || 0;
    return h > 0 && r > 0 ? Math.round(h * r) : null;
  }, [form.hours, form.hourly_rate]);
  const effectiveHourly = autoHourly !== null ? autoHourly : (Number(form.hourly_earned) || 0);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    await api.put(`/worker-payroll/${row.id}`, {
      worker_id: row.worker_id, month: row.month,
      days_present: Number(form.days_present),
      sun_fest_days: Number(form.sun_fest_days),
      sun_fest_earned: Number(form.sun_fest_earned),
      hours: Number(form.hours) || 0,
      hourly_rate: Number(form.hourly_rate) || 0,
      hourly_earned: Number(form.hourly_earned) || 0,
      remarks: form.remarks,
    });
    setBusy(false); onSaved();
  };
  return (
    <Modal title={`Edit · ${row.name} · ${row.month}`} onClose={onClose} testid="worker-payroll-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="text-xs text-stone-500">
          Monthly: ₹{row.monthly?.toLocaleString("en-IN")} · Day rate: ₹{row.day_rate} ·
          Hourly rate (Day ÷ 8): <b className="text-stone-800">₹{derivedRate}</b>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <In label="Days Present (of 30)" type="number" v={form.days_present} on={(v) => setForm({ ...form, days_present: v })} />
          <In label="Sun/Fest Days" type="number" v={form.sun_fest_days} on={(v) => setForm({ ...form, sun_fest_days: v })} />
          <In label="Sun/Fest Earned ₹" type="number" v={form.sun_fest_earned} on={(v) => setForm({ ...form, sun_fest_earned: v })} />
          <div />
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-2">
          <div className="kpi-label" style={{ color: "#9A6020" }}>Hourly Work · Rate auto-derived from Day Rate ÷ 8</div>
          <div className="grid grid-cols-3 gap-3">
            <In label="Hours Worked" type="number" v={form.hours} on={(v) => setForm({ ...form, hours: v })} placeholder="e.g. 8" />
            <div>
              <div className="kpi-label mb-1.5">Hourly Rate ₹/hr</div>
              <input type="number" value={form.hourly_rate ?? 0}
                     onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                     className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm bg-white focus:outline-none focus:border-primary"
                     data-testid="hourly-rate-input" />
              <div className="text-[10px] text-stone-500 mt-0.5">Auto: ₹{derivedRate} · edit to override</div>
            </div>
            <div>
              <div className="kpi-label mb-1.5">Hourly Earned ₹</div>
              {autoHourly !== null ? (
                <div className="px-3 py-2 border border-amber-300 rounded-md text-sm bg-white font-semibold" data-testid="hourly-earned-auto">
                  ₹{autoHourly.toLocaleString("en-IN")}
                  <span className="ml-1 text-[10px] font-normal text-stone-500">auto</span>
                </div>
              ) : (
                <input type="number" value={form.hourly_earned ?? 0} onChange={(e) => setForm({ ...form, hourly_earned: e.target.value })}
                       placeholder="manual ₹"
                       className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary"
                       data-testid="hourly-earned-manual" />
              )}
            </div>
          </div>
          <div className="text-[11px] text-stone-600">
            {autoHourly !== null
              ? <>Auto-computed: <b>{form.hours} hrs × ₹{form.hourly_rate} = ₹{autoHourly.toLocaleString("en-IN")}</b></>
              : <>Enter <b>Hours Worked</b> — Hourly Earned will auto-calculate at ₹{form.hourly_rate}/hr.</>}
          </div>
        </div>

        <label><div className="kpi-label mb-1.5">Remarks</div>
          <textarea rows="2" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
        </label>

        <div className="text-xs text-stone-500">
          Total Payable preview: <b className="text-stone-800">
            ₹{(Math.round((Number(row.monthly||0) * Number(form.days_present||0) / 30)) + Number(form.sun_fest_earned||0) + effectiveHourly).toLocaleString("en-IN")}
          </b>
        </div>

        <button type="submit" disabled={busy} data-testid="save-worker-payroll-button"
                className="w-full py-2 rounded-md text-white font-semibold" style={{ background: "var(--color-primary)" }}>
          {busy ? "Saving…" : "Recalculate & Save"}
        </button>
      </form>
    </Modal>
  );
}

export function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-white rounded-xl w-full max-w-xl p-6 my-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-xl font-bold">{title}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function In({ label, type = "text", v, on, required, placeholder }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1.5">{label}</div>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} required={required} placeholder={placeholder}
             className="w-full px-3 py-2 border border-stone-200 rounded-md text-sm focus:outline-none focus:border-primary" />
    </label>
  );
}
